import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader, Trash2, MessageSquare, Send, Heart, TrendingUp,
} from 'lucide-react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { useTheme } from '../context/ThemeContext';
import { formatPrice } from '../utils/formatters';
import TradeAnalysisPanel from '../components/mentorship/TradeAnalysisPanel';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function GlowCard({ color, style, children, className = '', onClick }) {
  return (
    <div onClick={onClick} className={`app-card rounded-2xl ${className}`} style={{
      background: `linear-gradient(135deg, ${color}14 0%, ${color}04 100%)`,
      border: `1px solid ${color}30`,
      ...style,
    }}>
      {children}
    </div>
  );
}

const TABS = ['Overview', 'Trades', 'Behavior', 'Notes & Comments'];
const RANGES = ['30D', '90D', '1Y', 'ALL'];

export default function StudentProfilePage() {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { studentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const ac = colors.accent;

  const relationshipId = location.state?.relationshipId;
  const fullName = location.state?.fullName;

  const [tab, setTab] = useState('Overview');
  const [health, setHealth] = useState(null);
  const [trades, setTrades] = useState([]);
  const [equityCurve, setEquityCurve] = useState({ student: [], nifty50: [] });
  const [range, setRange] = useState('30D');
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [healthRes, tradesRes] = await Promise.allSettled([
        axios.get(`${API}/mentorship/students/${studentId}/behavioral-health`),
        axios.get(`${API}/mentorship/students/${studentId}/trade-history`),
      ]);
      if (healthRes.status === 'fulfilled') setHealth(healthRes.value.data);
      if (tradesRes.status === 'fulfilled') setTrades(tradesRes.value.data);
    } finally { setLoading(false); }
  }, [studentId]);

  const loadEquityCurve = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mentorship/students/${studentId}/equity-curve`, { params: { range } });
      setEquityCurve(data);
    } catch { /* silent */ }
  }, [studentId, range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEquityCurve(); }, [loadEquityCurve]);

  const handleRevoke = async () => {
    if (!relationshipId) return;
    try { await axios.delete(`${API}/mentorship/relationships/${relationshipId}`); navigate('/mentorship'); }
    catch { import('react-hot-toast').then(m => m.default.error('Failed to remove student')); }
  };

  const pnlColor = (v) => Number(v) >= 0 ? '#16C784' : '#ef4444';

  // Merge student + nifty curves by date for the chart
  const chartData = (() => {
    const map = {};
    equityCurve.student.forEach(p => { map[p.date] = { date: p.date, student: p.return_pct }; });
    equityCurve.nifty50.forEach(p => {
      map[p.date] = { ...(map[p.date] || { date: p.date }), nifty: p.return_pct };
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  })();

  if (selectedTrade) {
    return (
      <TradeAnalysisPanel
        trade={selectedTrade}
        relationshipId={relationshipId}
        onBack={() => setSelectedTrade(null)}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/mentorship')} className="app-card p-1.5 rounded-md" style={{ background: `linear-gradient(135deg, ${ac}12 0%, ${ac}04 100%)` }}>
          <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-[18px] font-bold" style={{ color: colors.primaryText }}>{fullName || health?.full_name || 'Student'}</h1>
            {health && <StatusChip status={health.status} />}
          </div>
          <p className="text-[12px]" style={{ color: colors.secondaryText }}>Student overview</p>
        </div>
        <button onClick={handleRevoke} className="p-1.5 rounded-lg" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}>
          <Trash2 className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b overflow-x-auto" style={{ borderColor: colors.border }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className="px-3 py-2 text-[13px] font-semibold transition-colors relative flex-shrink-0 whitespace-nowrap"
            style={{ color: tab === t ? ac : colors.secondaryText }}>
            {t}
            {tab === t && <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ background: ac }} />}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader className="w-5 h-5 animate-spin" style={{ color: colors.secondaryText }} /></div>
      ) : tab === 'Overview' ? (
        <div className="space-y-4">
          {health && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { label: 'Discipline Score', value: `${health.discipline_score}/100`, color: health.discipline_score >= 70 ? '#16C784' : health.discipline_score >= 50 ? '#f59e0b' : '#ef4444' },
                { label: 'Win Rate', value: `${health.win_rate}%`, color: health.win_rate >= 50 ? '#16C784' : '#ef4444' },
                { label: 'Trades', value: health.total_trades, color: ac },
                { label: 'Capital Tier', value: health.capital_tier, color: ac },
              ].map(({ label, value, color }) => (
                <GlowCard key={label} color={color} style={{ padding: 12 }}>
                  <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>{label}</p>
                  <p className="text-[18px] font-bold mt-0.5" style={{ color }}>{value}</p>
                </GlowCard>
              ))}
            </div>
          )}

          {/* Equity curve */}
          <GlowCard color={ac} style={{ padding: 14 }}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[13px] font-semibold flex items-center gap-1.5" style={{ color: colors.primaryText }}>
                <TrendingUp className="w-4 h-4" style={{ color: ac }} /> Equity Curve
              </p>
              <div className="flex gap-1 p-0.5 rounded-lg" style={{ background: colors.inputBg }}>
                {RANGES.map(r => (
                  <button key={r} onClick={() => setRange(r)}
                    className="px-2.5 py-1 rounded-md text-[11px] font-semibold"
                    style={{ background: range === r ? ac : 'transparent', color: range === r ? '#fff' : colors.secondaryText }}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
            {chartData.length === 0 ? (
              <div className="flex items-center justify-center py-10">
                <p className="text-[12px]" style={{ color: colors.secondaryText }}>No trade data yet for this range</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'} vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: colors.secondaryText }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 9, fill: colors.secondaryText }} tickLine={false} axisLine={false} tickFormatter={v => `${v}%`} width={42} />
                  <Tooltip
                    contentStyle={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: colors.secondaryText }}
                    formatter={(v) => `${v}%`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="student" name={fullName || 'Student'} stroke={ac} strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="nifty" name="Nifty 50" stroke={colors.secondaryText} strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            )}
          </GlowCard>

          {/* Discipline breakdown */}
          {health?.component_scores && (
            <GlowCard color={ac} style={{ padding: 14 }}>
              <p className="text-[13px] font-semibold mb-3" style={{ color: colors.primaryText }}>Discipline Breakdown</p>
              <div className="space-y-2.5">
                {Object.entries({
                  'Risk Management': health.component_scores.risk_profile,
                  'Strategy Consistency': health.component_scores.strategy,
                  'Emotional Control': health.component_scores.emotion,
                  'Trade Management': health.component_scores.risk_reward,
                  'Overtrading': health.component_scores.frequency,
                }).map(([label, value]) => {
                  const v = value ?? 50;
                  const color = v >= 70 ? '#16C784' : v >= 50 ? '#f59e0b' : '#ef4444';
                  return (
                    <div key={label}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[12px]" style={{ color: colors.secondaryText }}>{label}</span>
                        <span className="text-[12px] font-bold" style={{ color }}>{v}/100</span>
                      </div>
                      <div className="h-1.5 rounded-full" style={{ background: colors.inputBg }}>
                        <div className="h-1.5 rounded-full" style={{ width: `${v}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </GlowCard>
          )}

          {/* Recent trades preview */}
          <GlowCard color={ac} style={{ padding: 14 }}>
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Recent Trades</p>
              <button onClick={() => setTab('Trades')} className="text-[12px] font-semibold" style={{ color: ac }}>View All</button>
            </div>
            <div className="space-y-1.5">
              {trades.slice(0, 5).map(t => (
                <div key={t.trade_id} onClick={() => setSelectedTrade(t)}
                  className="flex items-center justify-between px-3 py-1.5 rounded-lg cursor-pointer"
                  style={{ background: colors.inputBg, border: `1px solid ${colors.border}` }}>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{(t.symbol || '').replace('.NS', '')}</p>
                    <p className="text-[11px]" style={{ color: colors.secondaryText }}>{t.product_type} · {t.trade_type}</p>
                  </div>
                  <p className="text-[13px] font-bold flex-shrink-0" style={{ color: pnlColor(t.pnl) }}>{t.pnl != null ? formatPrice(t.pnl) : '—'}</p>
                </div>
              ))}
              {trades.length === 0 && <p className="text-[12px] text-center py-2" style={{ color: colors.secondaryText }}>No trades yet</p>}
            </div>
          </GlowCard>
        </div>
      ) : tab === 'Trades' ? (
        trades.length === 0 ? (
          <GlowCard color={ac} style={{ padding: 24, textAlign: 'center' }}>
            <p className="text-[12px]" style={{ color: colors.secondaryText }}>No trades yet</p>
          </GlowCard>
        ) : (
          <>
            {/* Desktop/tablet: table */}
            <GlowCard color={ac} style={{ padding: 0, overflow: 'hidden' }} className="hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full text-left" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                      {['Stock', 'Entry / Exit', 'P&L', 'R:R', 'Status'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {trades.map(t => (
                      <tr key={t.trade_id} onClick={() => setSelectedTrade(t)}
                        className="cursor-pointer transition-colors hover:bg-white/[0.02]"
                        style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td className="px-4 py-3">
                          <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{(t.symbol || '').replace('.NS', '')}</p>
                          <p className="text-[11px]" style={{ color: colors.secondaryText }}>{t.product_type} · {t.trade_type}</p>
                        </td>
                        <td className="px-4 py-3 text-[12px]" style={{ color: colors.secondaryText }}>
                          {formatPrice(t.price)} → {t.exit_price != null ? formatPrice(t.exit_price) : '—'}
                        </td>
                        <td className="px-4 py-3 text-[13px] font-bold" style={{ color: pnlColor(t.pnl) }}>
                          {t.pnl != null ? formatPrice(t.pnl) : '—'}
                        </td>
                        <td className="px-4 py-3 text-[12px]" style={{ color: colors.primaryText }}>
                          {t.actual_r != null ? `${Number(t.actual_r).toFixed(2)}R` : '—'}
                        </td>
                        <td className="px-4 py-3 text-[12px]" style={{ color: colors.secondaryText }}>{t.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GlowCard>

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
              {trades.map(t => (
                <GlowCard key={t.trade_id} color={ac} style={{ padding: '10px 12px', cursor: 'pointer' }} onClick={() => setSelectedTrade(t)}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{(t.symbol || '').replace('.NS', '')}</p>
                      <p className="text-[11px]" style={{ color: colors.secondaryText }}>{t.product_type} · {t.trade_type}</p>
                    </div>
                    <p className="text-[14px] font-bold flex-shrink-0 ml-2" style={{ color: pnlColor(t.pnl) }}>
                      {t.pnl != null ? formatPrice(t.pnl) : '—'}
                    </p>
                  </div>
                  <div className="flex items-center justify-between text-[11px]" style={{ color: colors.secondaryText }}>
                    <span>{formatPrice(t.price)} → {t.exit_price != null ? formatPrice(t.exit_price) : '—'}</span>
                    <span>{t.actual_r != null ? `${Number(t.actual_r).toFixed(2)}R` : '—'} · {t.status}</span>
                  </div>
                </GlowCard>
              ))}
            </div>
          </>
        )
      ) : tab === 'Behavior' ? (
        <GlowCard color={ac} style={{ padding: 14 }}>
          <p className="text-[13px] font-semibold mb-3" style={{ color: colors.primaryText }}>Behavior Profile</p>
          {health?.profile ? (
            <div className="space-y-2">
              <p className="text-[13px]" style={{ color: colors.primaryText }}>{health.profile.profile_type}</p>
              {(health.profile.signals || []).map((s, i) => (
                <p key={i} className="text-[12px]" style={{ color: colors.secondaryText }}>• {s}</p>
              ))}
            </div>
          ) : <p className="text-[12px]" style={{ color: colors.secondaryText }}>No behavior data yet</p>}
        </GlowCard>
      ) : (
        <CommentsPanel relationshipId={relationshipId} colors={colors} ac={ac} />
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const map = { Excellent: '#16C784', Good: '#3b82f6', 'Needs Focus': '#f59e0b', 'At Risk': '#ef4444' };
  const color = map[status] || '#6b7280';
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
      style={{ background: `${color}18`, color, border: `1px solid ${color}35` }}>
      {status}
    </span>
  );
}

function CommentsPanel({ relationshipId, colors, ac }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mentorship/relationships/${relationshipId}/comments`);
      setComments(data);
    } finally { setLoading(false); }
  }, [relationshipId]);

  useEffect(() => { if (relationshipId) load(); }, [relationshipId, load]);

  const handlePost = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/mentorship/relationships/${relationshipId}/comments`, {
        body: text.trim(), parent_comment_id: replyTo,
      });
      setText(''); setReplyTo(null);
      load();
    } catch { import('react-hot-toast').then(m => m.default.error('Failed to post comment')); }
    finally { setPosting(false); }
  };

  const handleLike = async (id) => {
    try {
      const { data } = await axios.post(`${API}/mentorship/comments/${id}/like`);
      setComments(prev => updateLike(prev, id, data));
    } catch { /* silent */ }
  };

  const updateLike = (list, id, data) => list.map(c => {
    if (c.id === id) return { ...c, like_count: data.like_count, liked_by_me: data.liked_by_me };
    if (c.replies?.length) return { ...c, replies: updateLike(c.replies, id, data) };
    return c;
  });

  const Comment = ({ c, depth = 0 }) => (
    <div style={{ marginLeft: depth * 24 }}>
      <div className="px-3 py-2 rounded-lg" style={{ background: colors.inputBg, border: `1px solid ${colors.border}` }}>
        <div className="flex items-center justify-between">
          <p className="text-[12px] font-semibold" style={{ color: colors.primaryText }}>{c.author_name}</p>
          <p className="text-[11px]" style={{ color: colors.secondaryText }}>{new Date(c.created_at).toLocaleString()}</p>
        </div>
        <p className="text-[13px] mt-1" style={{ color: colors.primaryText }}>{c.body}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <button onClick={() => handleLike(c.id)} className="flex items-center gap-1 text-[11px]" style={{ color: c.liked_by_me ? '#ef4444' : colors.secondaryText }}>
            <Heart className="w-3 h-3" fill={c.liked_by_me ? '#ef4444' : 'none'} /> {c.like_count}
          </button>
          <button onClick={() => setReplyTo(c.id)} className="text-[11px]" style={{ color: ac }}>Reply</button>
        </div>
      </div>
      {(c.replies || []).map(r => <div key={r.id} className="mt-1.5"><Comment c={r} depth={depth + 1} /></div>)}
    </div>
  );

  return (
    <div className="rounded-2xl app-card" style={{ background: `linear-gradient(135deg, ${ac}14 0%, ${ac}04 100%)`, border: `1px solid ${ac}30`, padding: 14 }}>
      <p className="text-[13px] font-semibold mb-2.5 flex items-center gap-1.5" style={{ color: colors.primaryText }}>
        <MessageSquare className="w-4 h-4" style={{ color: ac }} /> Notes & Comments
      </p>
      <div className="flex gap-2 mb-3">
        <input
          type="text" placeholder={replyTo ? 'Write a reply...' : 'Write a comment...'}
          value={text} onChange={e => setText(e.target.value)}
          className="flex-1 px-3 py-2 rounded-lg text-[13px]"
          style={{ background: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}
        />
        {replyTo && (
          <button onClick={() => setReplyTo(null)} className="px-2 rounded-lg text-[11px]" style={{ color: colors.secondaryText, border: `1px solid ${colors.border}` }}>
            Cancel
          </button>
        )}
        <button onClick={handlePost} disabled={posting || !text.trim()}
          className="px-3 rounded-lg" style={{ background: `linear-gradient(135deg, ${ac}, #8b5cf6)`, boxShadow: `0 2px 10px ${ac}40`, opacity: !text.trim() ? 0.5 : 1 }}>
          {posting ? <Loader className="w-4 h-4 animate-spin text-white" /> : <Send className="w-4 h-4 text-white" />}
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-6"><Loader className="w-4 h-4 animate-spin" style={{ color: colors.secondaryText }} /></div>
      ) : comments.length === 0 ? (
        <p className="text-[12px] text-center py-4" style={{ color: colors.secondaryText }}>No comments yet — start the conversation</p>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {comments.map(c => <Comment key={c.id} c={c} />)}
        </div>
      )}
    </div>
  );
}
